import withCalendar, {Calendar} from "@COMPONENTS/hoc/withCalendar";
import {Select} from "@FRAMEWORK/ui/mobile/select";
import useContentsHoliday from "@QUERIES/essential/contents/useContentsHoliday";
import dayjs, {Dayjs} from "dayjs";
import styled, {css} from "styled-components";

interface DayProps {
	isActive?: boolean;
	CheckedDay?: boolean;
	today?: boolean;
	holiday?: boolean;
	types?: "basic" | "schedule";
	rev?: "can" | "wait" | "end";
}

const CalendarWrap = styled.div<{types?: "basic" | "schedule"}>`
	${props => {
		if (props.types === "basic") {
			return css`
				width: 100%;
				margin: 0 auto;
			`;
		}
		if (props.types === "schedule") {
			return css`
				width: 100%;
				margin: 0 auto;
			`;
		}
	}}
`;

const CalendarBox = styled.div<DayProps>`
	width: 100%;
	background-color: #fff;
	${props =>
		props.types === "basic" &&
		css`
			table {
				th {
					height: 22px;
					background-color: #fff;
					text-align: center;
					&:not(:last-child),
					&:last-child {
						border: none;
					}
				}
				td {
					height: 22px;
					padding: 0;
					&:not(:last-child),
					&:last-child {
						border: none;
					}
				}
			}
		`}
	${props =>
		props.types === "schedule" &&
		css`
			table {
				th {
					height: 50px;
					background-color: #fff;
					text-align: center;
					border-top: 0;
				}
				td {
					position: relative;
					height: 50px;
					padding: 0;
					border: 1px solid var(--line-color);
					border-collapse: collapse;

					&:first-child {
						color: var(--red-color);
					}
					&:last-child {
						color: var(--blue-color);
					}

					span {
						position: relative;
						display: inline-flex;
						align-items: flex-start;
						padding-top: 10px;
						font-size: 12px;
					}
				}
			}
		`}
`;

const CalendarMonth = styled.div`
	position: relative;
	display: flex;
	width: 100%;
	justify-content: center;
	em {
		font-size: 22px;
		font-weight: bold;
		color: var(--emp-color);
		line-height: 1;
	}
	label {
		font-size: var(--medium-size);
		font-weight: normal;
	}
	ul {
		top: 50px;
		left: 50%;
		width: 100%;
		max-height: 155px;
		transform: translateX(-50%);
		> li {
			justify-content: center;
			padding: 0;
		}
	}
	> div {
		width: auto;
	}
`;

const MonthButton = styled.button<{types?: "prev" | "next"}>`
	position: absolute;
	top: 13px;
	width: 12px;
	height: 12px;
	border-left: 1px solid var(--emp-color);
	border-bottom: 1px solid var(--emp-color);
	background-color: transparent;

	&:disabled {
		border-color: var(--bl-color);
	}

	${props =>
		props.types === "prev"
			? `
                left: 20%;
                transform: rotate(45deg);
            `
			: `
                right: 20%;
                transform: rotate(-135deg);
            `}
`;

const TableCalendar = styled.table`
	width: 100%;
	border-bottom: 1px solid var(--line-color);
	table-layout: fixed;
`;

const Month = styled.th<DayProps>`
	height: 22px;
	padding: 10px;
	box-sizing: border-box;
	border-top: 1px solid var(--line-color);
	font-size: 12px;
	color: var(--emp-color);
	text-align: center;
	&:first-child {
		color: #ff5c46;
	}
	&:last-child {
		color: #4567ad;
	}
	${props => {
		if (props.types === "basic") {
			return css`
				font-size: 12px;
			`;
		}
	}}
`;

const ProductDays = styled.td<DayProps>`
	position: relative;
	box-sizing: border-box;
	height: 22px;
	color: ${props => (props.isActive ? "var(--emp-color)" : "#aaa")};
	vertical-align: top;
	text-align: left;
	cursor: ${props => (props.isActive ? "pointer" : "initial")};

	${props => {
		if (props.types === "basic") {
			return css`
				&:first-child {
					> span {
						color: ${props.rev ? "var(--emp-color)" : "var(--red-color)"};
					}
				}
				&:last-child {
					> span {
						color: ${props.rev ? "var(--emp-color)" : "var(--blue-color)"};
					}
				}
				> span {
					font-size: 12px;
					color: #aaa;
				}
			`;
		}
		if (props.types === "schedule") {
			return css`
				${props.CheckedDay &&
				css`
					border: none;
					> span {
						border-color: var(--emp-color);
					}
				`};
				> span::after {
					display: block;
					top: 16px;
				}
			`;
		}
	}}
	> span {
		border: 1px solid transparent;
		border-color: ${props => (props.CheckedDay ? "var(--emp-color)" : "transparent")};
		box-sizing: border-box;
		${props => {
			if (props.rev === "can" && props.types === "basic") {
				return css`
					background-color: var(--yellow-color);
					color: var(--emp-color);
				`;
			}
			if (props.rev === "end" && props.types === "basic") {
				return css`
					background-color: var(--line-color);
					color: var(--emp-color);
				`;
			}
			if (props.rev === "wait" && props.types === "basic") {
				return css`
					background-color: #a4bdf0;
					color: var(--emp-color);
				`;
			}
		}}
	}
`;

const Day = styled.span<DayProps>`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 100%;
	z-index: 1;
	color: ${props => (props.holiday ? "var(--red-color)" : "")};
	${props =>
		props.today &&
		css`
			color: #fff;
			&::after {
				position: absolute;
				top: 7px;
				left: 50%;
				z-index: -1;
				transform: translate(-50%, -50%);
				display: none;
				width: 25px;
				height: 25px;
				border-radius: 50%;
				background-color: var(--emp-color);
				content: "";
			}
		`}
`;

const LocalFilterCalendar = withCalendar((props: Calendar & {data: {handleClose: () => void}}) => {
	const types = "schedule";
	const today = dayjs();
	const {
		date,
		isNext,
		isPrevious,
		rangeMonth,
		dayList,
		month,
		dateChange,
		monthChange,
		nextMonth,
		prevMonth,
		data: {handleClose},
	} = props;

	const holidayFetch = useContentsHoliday(dayjs(month).format("YYYY"), {staleTime: 1000 * 60 * 5});
	const holiday = holidayFetch.data?.body ?? [];

	return (
		<CalendarWrap types={types}>
			<CalendarBox types={types}>
				<CalendarMonth>
					<Select
						types="calendar"
						width="auto"
						onChange={e => monthChange(e.target.value)}
						value={month.format("YYYYMMDD")}
					>
						{rangeMonth?.map((day: Dayjs, index) => (
							<option key={index} value={day.format("YYYYMMDD")}>
								{day.format("YYYY.MM")}
							</option>
						))}
					</Select>
					{isPrevious && (
						<MonthButton types="prev" onClick={prevMonth}>
							<span>이전달</span>
						</MonthButton>
					)}
					{isNext && (
						<MonthButton types="next" onClick={nextMonth}>
							<span>다음달</span>
						</MonthButton>
					)}
				</CalendarMonth>
				<TableCalendar>
					<thead>
						<tr>
							<Month types={types}>일</Month>
							<Month types={types}>월</Month>
							<Month types={types}>화</Month>
							<Month types={types}>수</Month>
							<Month types={types}>목</Month>
							<Month types={types}>금</Month>
							<Month types={types}>토</Month>
						</tr>
					</thead>
					<tbody>
						{dayList?.map((week, index) => (
							<tr key={index}>
								{week.map((day, weekIdx) => {
									const dayFormat = day?.format("YYYYMMDD");
									const isHoliday = !!holiday.find(v => v.calDt === dayFormat);
									return (
										<ProductDays
											key={weekIdx}
											types={types}
											isActive={!(day < today.add(-1, "day"))}
											onClick={() => {
												if (!(day < today.add(-1, "day"))) {
													dateChange(day);
													handleClose();
												}
											}}
											CheckedDay={date && date.isSame(day)}
										>
											<Day today={day && today.isSame(day, "day")} holiday={isHoliday}>
												{day && day.format("DD")}
											</Day>
										</ProductDays>
									);
								})}
							</tr>
						))}
					</tbody>
				</TableCalendar>
			</CalendarBox>
		</CalendarWrap>
	);
});

export default LocalFilterCalendar;
